/* eslint-disable react-hooks/exhaustive-deps */
import {FC, Fragment, useCallback, useState} from 'react'
import {KTCard, KTCardBody} from '../../../_metronic/helpers'
import {ListViewProvider, useListView} from './core/ListViewProvider'
import {QueryRequestProvider} from './core/QueryRequestProvider'
import {QueryResponseProvider} from './core/QueryResponseProvider'
import PortfolioFilter from './PortfolioFilter'
import {DataTable} from './table/DataTable'
import {PageTitle} from '../../../_metronic/layout/core'
import {getDownloadLink} from './core/_requests'
import {initialQueryRequest} from './core/crud-helper'
import Swal from 'sweetalert2'
import axios from 'axios'
import UsersColumns from './table/columns/_column'
import React from 'react'
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd'

const UsersList: FC = () => {
  const [isExportLoading, setIsExportLoading] = useState(false)
  const [downloadLink, setDownloadLink] = useState('')
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const {selected} = useListView()
  const [loadingImageIndex, setLoadingImageIndex] = useState<any>(null)

  const columnsArray = UsersColumns(loadingImageIndex, setLoadingImageIndex)
  const previewWindowRef = React.useRef<Window | null>(null)
  const overviewUrl = process.env.REACT_APP_OVERVIEW_URL

  // Get all column names from usersColumns (excluding selection column)
  const allColumns = React.useMemo(
    () =>
      columnsArray
        ?.filter((col) => col.id !== 'selection') // Filter out selection column
        ?.map((col) => {
          let headerText = 'Unknown'
          if (typeof col.Header === 'string') {
            headerText = col.Header
          } else if (typeof col.Header === 'function') {
            try {
              // Call the header function to get the rendered element and its props
              const result = (col.Header as any)({} as any)
              headerText = result?.props?.title || 'Unknown'
            } catch {
              headerText = 'Unknown'
            }
          }
          return {
            id: String(col.id || col.accessor || ''),
            title: headerText,
            accessor: col.accessor || col.id,
          }
        }),
    [columnsArray]
  )

  // Initialize selected columns with all columns (including selection)
  React.useEffect(() => {
    if (allColumns.length > 0 && selectedColumns.length === 0) {
      setSelectedColumns(['selection', ...allColumns.map((col) => col.id)])
    }
  }, [allColumns, selectedColumns.length])

  const handleColumnToggle = (columnId: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId)
      } else {
        return [...prev, columnId]
      }
    })
  }

  // Test function to toggle dropdown visibility
  const [showDropdown, setShowDropdown] = useState(false)
  const [columnOrder, setColumnOrder] = useState<any[]>([])

  // Initialize column order when allColumns is loaded
  React.useEffect(() => {
    if (allColumns?.length > 0 && columnOrder?.length === 0) {
      setColumnOrder([...allColumns])
    }
  }, [allColumns, columnOrder?.length])

  // Handle drag end event from react-beautiful-dnd
  const handleDragEnd = (result: any) => {
    if (!result.destination) {
      return // Dropped outside the list
    }

    const sourceIndex = result.source.index
    const destinationIndex = result.destination.index

    if (sourceIndex === destinationIndex) {
      return // Dropped in the same position
    }

    // Reorder columns
    const newOrder = Array.from(columnOrder)
    const [reorderedItem] = newOrder.splice(sourceIndex, 1)
    newOrder.splice(destinationIndex, 0, reorderedItem)

    setColumnOrder(newOrder)

    // Also update selectedColumns to match the new order
    const newSelectedColumns = [
      'selection',
      ...newOrder.map((c) => String(c.id || c.accessor || '')),
    ]
    setSelectedColumns(newSelectedColumns)
  }

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDropdown && !(event.target as Element).closest('.dropdown')) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showDropdown])

  const onDownload = useCallback(
    async (isExportValue) => {
      // Handle Preview PDF by opening the new preview page URL
      const previewUrl = `/portfolio/preview`
      
      // Store selected projects in session storage for preview page
      sessionStorage.setItem('previewProjects', JSON.stringify(selected))
      
      window.open(previewUrl, '_blank')

      setDownloadLink('')
      const source = axios.CancelToken.source()
      setIsExportLoading(true)
      const payload = {
        ...initialQueryRequest.state,
        isExport: isExportValue,
        filter: {
          project: selected,
        },
      }

      try {
        const response = await getDownloadLink(payload, source.token)
        setDownloadLink(response.pdf_url)

        if (response?.pdf_url) {
          window.open(response.pdf_url, '_blank')
        }
      } catch (error: any) {
        if (!axios.isCancel(error)) {
          console.error('Download failed', error)
        }
      } finally {
        setIsExportLoading(false)
      }
    },
    [selected]
  )

  // Message handler no longer needed as Preview Page handles its own generation

  return (
    <Fragment>
      <PageTitle breadcrumbs={[]}>Portfolio</PageTitle>
      <KTCard className='my-7'>
        <KTCardBody className='p-0 p-md-7'>
          <div className='col-md-12 text-center mx-2 mt-0 mb-4 d-flex justify-content-between align-items-center'>
            <div className='d-flex gap-3'>
              <button
                type='button'
                className={`btn btn-primary btn-sm ${
                  isExportLoading || (selected?.length || 0) === 0 ? '' : 'cursor-pointer'
                }`}
                onClick={() => onDownload(true)}
                disabled={isExportLoading || (selected?.length || 0) === 0}
              >
                {isExportLoading ? (
                  <span className='d-flex align-items-center gap-2'>
                    <span
                      className='spinner-border spinner-border-sm'
                      role='status'
                      aria-hidden='true'
                    />
                    <span>Generating...</span>
                  </span>
                ) : (
                  <span
                    className={
                      isExportLoading || (selected?.length || 0) === 0 ? '' : 'cursor-pointer'
                    }
                  >
                    Preview
                  </span>
                )}
              </button>
              {downloadLink && (
                <button
                  type='button'
                  className='btn btn-secondary btn-sm cursor-pointer'
                  onClick={() => {
                    navigator.clipboard.writeText(downloadLink)
                    Swal.fire({
                      toast: true,
                      position: 'top-end',
                      icon: 'success',
                      title: 'Link copied to clipboard',
                      showConfirmButton: false,
                      timer: 3000,
                    })
                  }}
                >
                  <span className='cursor-pointer'>Copy Link</span>
                </button>
              )}
            </div>

            {/* Column Dropdown */}
            <div className='dropdown'>
              <button
                className='btn btn-secondary btn-sm'
                type='button'
                id='columnDropdown'
                onClick={() => {
                  setShowDropdown(!showDropdown)
                }}
              >
                Columns
                <i className={`fas fa-chevron-${showDropdown ? 'up' : 'down'} ms-2`}></i>
              </button>
              {showDropdown && (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId='columns'>
                    {(provided, snapshot) => (
                      <ul
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className='dropdown-menu show'
                        aria-labelledby='columnDropdown'
                        style={{
                          minWidth: '300px',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          zIndex: 9999,
                          position: 'absolute',
                          right: '0',
                          display: 'block',
                          backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'white',
                        }}
                      >
                        <li className='dropdown-header d-flex align-items-center justify-content-between'>
                          <strong>Drag to Reorder Columns</strong>
                          <button
                            type='button'
                            className='btn btn-icon btn-active-light-primary btn-sm p-0'
                            title='Reset to Default'
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedColumns(['selection', ...allColumns.map((col) => col.id)])
                              setColumnOrder([...allColumns])
                            }}
                            style={{width: '25px', height: '25px'}}
                          >
                            <i className='fas fa-sync-alt fs-7 text-primary'></i>
                          </button>
                        </li>
                        <li>
                          <hr className='dropdown-divider' />
                        </li>
                        {columnOrder.map((column, index) => {
                          const columnId = String(column.id || column.accessor || '')

                          return (
                            <Draggable key={columnId} draggableId={columnId} index={index}>
                              {(provided, snapshot) => (
                                <li
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    backgroundColor: snapshot.isDragging
                                      ? '#e3f2fd'
                                      : 'transparent',
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    marginBottom: '2px',
                                    border: snapshot.isDragging
                                      ? '2px solid #2196f3'
                                      : '1px solid #e0e0e0',
                                    boxShadow: snapshot.isDragging
                                      ? '0 4px 12px rgba(33,150,243,0.3)'
                                      : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div className='dropdown-item' style={{width: '100%'}}>
                                    <div className='d-flex align-items-center justify-content-between'>
                                      <div className='form-check'>
                                        <input
                                          className='form-check-input'
                                          type='checkbox'
                                          id={columnId}
                                          checked={selectedColumns.includes(columnId)}
                                          onChange={() => {
                                            handleColumnToggle(columnId)
                                          }}
                                        />
                                        <label className='form-check-label ms-2' htmlFor={columnId}>
                                          {column.title}
                                        </label>
                                      </div>
                                      <div className='ms-2'>
                                        <i className='fas fa-grip-vertical text-muted'></i>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
          <DataTable
            selectedColumns={selectedColumns}
            columnOrder={columnOrder}
            loadingImageIndex={loadingImageIndex}
            setLoadingImageIndex={setLoadingImageIndex}
          />
        </KTCardBody>
      </KTCard>
    </Fragment>
  )
}

export default function PortFolioWrapper() {
  return (
    <div className='content flex-row-fluid'>
      <QueryRequestProvider>
        <QueryResponseProvider>
          <ListViewProvider>
            <PortfolioFilter />
            <UsersList />
          </ListViewProvider>
        </QueryResponseProvider>
      </QueryRequestProvider>
    </div>
  )
}
